"use client";

import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { PermissionGate } from "@/modules/auth/presentation/ui/PermissionGate";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { isDev } from "@/shared/lib/config";
import { parseISO } from "@/shared/lib/date-time";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { useSearch } from "@tanstack/react-router";
import { useMemo } from "react";
import {
  useDashboardAlertsQuery,
  useDashboardChartsQuery,
  useDashboardKPIQuery,
  useDashboardTablesQuery,
} from "../api/queries";
import { DashboardAlerts } from "../ui/DashboardAlerts";
import { DashboardCharts } from "../ui/DashboardCharts";
import { DashboardFilters, type DateRangePreset } from "../ui/DashboardFilters";
import { DashboardKPICards } from "../ui/DashboardKPICards";
import { DashboardQuickActions } from "../ui/DashboardQuickActions";
import { DashboardTables } from "../ui/DashboardTables";
import { ShadcnFoundationPreview } from "@/components/shadcn-foundation-preview";

function getDateRangeForPreset(
  preset: DateRangePreset,
  from?: string,
  to?: string,
): { fromDate?: Date; toDate?: Date } {
  if (preset === "custom" && from && to) {
    return {
      fromDate: parseISO(from),
      toDate: parseISO(to),
    };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  switch (preset) {
    case "today": {
      const to = new Date(today);
      to.setHours(23, 59, 59, 999);
      return { fromDate: today, toDate: to };
    }
    case "thisWeek": {
      const from = new Date(today);
      const dayOfWeek = from.getDay();
      const diff = from.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Monday
      from.setDate(diff);
      const to = new Date(today);
      to.setHours(23, 59, 59, 999);
      return { fromDate: from, toDate: to };
    }
    case "thisMonth": {
      const from = new Date(today.getFullYear(), today.getMonth(), 1);
      const to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      to.setHours(23, 59, 59, 999);
      return { fromDate: from, toDate: to };
    }
    default:
      return {};
  }
}

export function DashboardPage() {
  const search = useSearch({ from: "/app/dashboard" }) as {
    dateRangePreset?: DateRangePreset;
    dateFrom?: string;
    dateTo?: string;
    branchId?: string;
  };

  const dateRangePreset = search.dateRangePreset || "today";
  const dateFrom = search.dateFrom;
  const dateTo = search.dateTo;
  const branchId = search.branchId;

  const dateRange = useMemo(
    () => getDateRangeForPreset(dateRangePreset, dateFrom, dateTo),
    [dateRangePreset, dateFrom, dateTo],
  );

  const dashboardParams = useMemo(
    () => ({
      fromDate: dateRange.fromDate,
      toDate: dateRange.toDate,
      branchId,
    }),
    [dateRange.fromDate, dateRange.toDate, branchId],
  );

  const {
    data: kpiData,
    isLoading: isLoadingKPI,
    refetch: refetchKPI,
  } = useDashboardKPIQuery(dashboardParams);

  const {
    data: chartData,
    isLoading: isLoadingCharts,
    refetch: refetchCharts,
  } = useDashboardChartsQuery(dashboardParams);

  const {
    data: tableData,
    isLoading: isLoadingTables,
    refetch: refetchTables,
  } = useDashboardTablesQuery(dashboardParams);

  const {
    data: alertData,
    isLoading: isLoadingAlerts,
    refetch: refetchAlerts,
  } = useDashboardAlertsQuery(dashboardParams);

  const isLoading =
    isLoadingKPI || isLoadingCharts || isLoadingTables || isLoadingAlerts;

  const handleRefresh = () => {
    // Refetch all queries
    refetchKPI();
    refetchCharts();
    refetchTables();
    refetchAlerts();
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            {
              label: "ແຜງຄວບຄຸມ",
              to: "/app/dashboard",
            },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="font-bold text-2xl tracking-tight">ແຜງຄວບຄຸມ</h1>

          <DashboardFilters onRefresh={handleRefresh} isLoading={isLoading} />
        </div>

        <div className="space-y-6">
          {/* KPI Cards - แสดงเมื่อมี permission ดูรายงานยอดขาย, ออเดอร์, หรือ wallet */}
          <PermissionGate
            any={[
              Permissions.reports.sales.read,
              Permissions.orders.read,
              Permissions.reports.wallet.read,
              Permissions.wallet.read,
            ]}
          >
            <DashboardKPICards
              todaySalesLak={kpiData?.todaySalesLak}
              todayOrdersCount={kpiData?.todayOrdersCount}
              totalWalletLak={kpiData?.totalWalletLak}
              pendingOrdersCount={kpiData?.pendingOrdersCount}
              lowStockItemCount={kpiData?.lowStockItemCount}
              showLowStock={false}
              isLoading={isLoading}
            />
          </PermissionGate>

          <PermissionGate
            any={[
              Permissions.reports.inventory.read,
              Permissions.inventory.read,
            ]}
          >
            <DashboardKPICards
              lowStockItemCount={kpiData?.lowStockItemCount}
              showLowStock
              isLoading={isLoading}
            />
          </PermissionGate>

          {/* Charts - แสดงเมื่อมี permission ดูรายงานยอดขาย */}
          <PermissionGate any={[Permissions.reports.sales.read]}>
            <DashboardCharts
              salesOverTimeData={chartData?.salesOverTimeData}
              salesByBranchData={chartData?.salesByBranchData}
              ordersByStatusData={chartData?.ordersByStatusData}
              isLoading={isLoading}
            />
          </PermissionGate>

          {/* Tables - แสดงเมื่อมี permission ดูรายงานยอดขาย, ออเดอร์, หรือ wallet */}
          <PermissionGate
            any={[
              Permissions.reports.sales.read,
              Permissions.orders.read,
              Permissions.reports.wallet.read,
              Permissions.wallet.read,
            ]}
          >
            <DashboardTables
              topProducts={tableData?.topProducts}
              recentOrders={tableData?.recentOrders?.map((order) => ({
                ...order,
                createdAt:
                  order.createdAt instanceof Date
                    ? order.createdAt.toISOString()
                    : order.createdAt,
              }))}
              walletTransactions={tableData?.walletTransactions?.map((txn) => ({
                ...txn,
                createdAt:
                  txn.createdAt instanceof Date
                    ? txn.createdAt.toISOString()
                    : txn.createdAt,
              }))}
              isLoading={isLoading}
            />
          </PermissionGate>

          {/* Alerts - แสดงเมื่อมี permission ดูรายงานสต็อกหรือ wallet */}
          <PermissionGate
            any={[
              Permissions.reports.inventory.read,
              Permissions.inventory.read,
              Permissions.wallet.approve,
              Permissions.wallet.read,
            ]}
          >
            <DashboardAlerts
              expiringLots={alertData?.expiringLots?.map((lot) => ({
                ...lot,
                expiryDate:
                  lot.expiryDate instanceof Date
                    ? lot.expiryDate.toISOString()
                    : lot.expiryDate,
              }))}
              pendingApprovals={alertData?.pendingApprovals?.map(
                (approval) => ({
                  ...approval,
                  requestedAt:
                    approval.requestedAt instanceof Date
                      ? approval.requestedAt.toISOString()
                      : approval.requestedAt,
                }),
              )}
              pendingTopupCount={alertData?.pendingTopupCount}
              pendingRefundCount={alertData?.pendingRefundCount}
              lowStockItemCount={alertData?.lowStockItemCount}
              lowStockItems={alertData?.lowStockItems}
              isLoading={isLoading}
            />
          </PermissionGate>

          {/* Quick Actions */}
          <DashboardQuickActions />

          {isDev ? <ShadcnFoundationPreview /> : null}
        </div>
      </Main>
    </>
  );
}
